import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIstTelefon } from "../../hooks/useIstTelefon";

import { useDraftFromInvoice, useUpdateInvoiceRecord } from "../../hooks/useInvoices";
import { useCreateProjekt } from "../../hooks/usePlanung";
import {
  belegArtLabel, errorText, euro, istAngebotsbestaetigung,
  type DraftFromInvoiceModus, type InvoiceRegisterItem,
} from "../../lib/invoices";

/**
 * Aus einem Beleg den nächsten machen — und ihn duplizieren.
 *
 * Die Aktionen stehen als eine schlichte Reihe direkt unter dem Kopf des
 * Belegdetails (Redesign 02.09.2026): eine Hauptaktion je Belegart, der Rest
 * leise daneben. „Duplizieren" hieß vorher „Als Kopie neu anlegen" — dasselbe,
 * nur fand es niemand unter dem Namen (Benjamin, 02.09.2026).
 *
 * Die Belegnummer wird bewusst nicht mitkopiert: sie entsteht erst beim
 * Festschreiben, sonst rissen abgebrochene Entwürfe Lücken in den Kreis.
 * Für Desktop-Belege liegt das Quelldokument als YAML auf Bens Rechner und ist
 * vom Server nicht lesbar — dann kommt eine Sammelposition aus den
 * Registersummen, worauf der Hinweis unten aufmerksam macht.
 */
export default function InvoiceFolgebeleg({ item, onUpdated }: {
  item: InvoiceRegisterItem;
  onUpdated: (item: InvoiceRegisterItem) => void;
}) {
  const navigate = useNavigate();
  // Am Telefon kein neuer Beleg und kein Ändern (Benjamin, 20.10.2026) —
  // „Vom Kunden bestätigt" und das Projekt aus dem Angebot bleiben.
  const telefon = useIstTelefon();
  const ableiten = useDraftFromInvoice();
  const projektAnlegen = useCreateProjekt();
  const angebotZuordnen = useUpdateInvoiceRecord();
  const [hinweis, setHinweis] = useState("");
  const [projektTitel, setProjektTitel] = useState(item.projekt_title || `Projekt aus Angebot ${item.nummer}`);
  const [abschlagOffen, setAbschlagOffen] = useState(false);
  // Abschlag auf die Gesamtsumme (08.10.2026, Volker): Prozent ODER Betrag,
  // nicht mehr je Position.
  const [abschlagArt, setAbschlagArt] = useState<"prozent" | "betrag">("prozent");
  const [abschlagWert, setAbschlagWert] = useState("30");
  const wert = Number(abschlagWert.replace(",", ".")) || 0;
  const abschlagNetto = abschlagArt === "prozent" ? item.netto * wert / 100 : wert;
  const abschlagGueltig = wert > 0 && abschlagNetto > 0 && abschlagNetto <= item.netto + 0.005;

  const starten = async (modus: DraftFromInvoiceModus, abschlag?: { prozent?: number; netto?: number }) => {
    setHinweis("");
    try {
      const record = await ableiten.mutateAsync({ id: item.id, modus, abschlagProzent: abschlag?.prozent, abschlagNetto: abschlag?.netto });
      if (!record.positionen_uebernommen) {
        setHinweis("Der Quellbeleg stammt aus der Desktop-App — übernommen wurde nur die Summe als eine Position.");
        window.setTimeout(() => navigate(`/rechnungen/entwurf/${record.id}`), 2200);
        return;
      }
      navigate(`/rechnungen/entwurf/${record.id}`);
    } catch (error) {
      setHinweis(errorText(error));
    }
  };

  const istAngebot = item.kind === "quote";
  const istBestaetigung = istAngebotsbestaetigung(item.kind, item.source_quote_nummer);
  // Bestätigen lässt sich ein festgeschriebenes Angebot, das niemand abgelehnt hat
  // — gebraucht nur bei manchen Kunden, deshalb ein leiser Knopf (01.10.2026).
  const bestaetigbar = istAngebot && !istBestaetigung && !["entwurf", "abgelehnt", "storniert"].includes(item.status);
  const stornierbar = !istAngebot && item.status !== "entwurf" && item.status !== "storniert";
  const laeuft = ableiten.isPending;

  /** Kunde hat unterschrieben oder per Mail zugestimmt — Angebot und Projekt ziehen mit. */
  const kundeHatBestaetigt = async () => {
    setHinweis("");
    try {
      const aktualisiert = await angebotZuordnen.mutateAsync({ id: item.id, patch: { status: "angenommen" } });
      onUpdated(aktualisiert);
      const folgen = aktualisiert.bestaetigung_folgen;
      const teile = ["Als vom Kunden bestätigt vermerkt."];
      if (folgen?.angebot_angenommen) teile.push(`Angebot ${folgen.angebot} ist angenommen.`);
      if (folgen?.projekt_aktiv) teile.push("Das Projekt steht jetzt auf aktiv.");
      setHinweis(teile.join(" "));
    } catch (error) {
      setHinweis(errorText(error));
    }
  };

  const projektAusAngebot = async () => {
    const titel = projektTitel.trim();
    if (!titel) return setHinweis("Bitte gib dem Projekt einen Namen.");
    if (!item.kunde_id) return setHinweis("Das Angebot braucht zuerst eine Kundenzuordnung.");
    setHinweis("");
    try {
      const projekt = await projektAnlegen.mutateAsync({
        title: titel,
        kunde_id: item.kunde_id,
        status: "🟢 beauftragt",
        notes: `Erstellt aus Angebot ${item.nummer}.`,
        nettowert: item.netto,
      });
      const aktualisiert = await angebotZuordnen.mutateAsync({
        id: item.id,
        patch: { projekt_id: String(projekt.id), status: "angenommen" },
      });
      onUpdated(aktualisiert);
      setHinweis(`Projekt „${titel}“ ist angelegt und mit ${item.nummer} verbunden.`);
    } catch (error) {
      setHinweis(errorText(error));
    }
  };

  return (
    <section className="invoice-aktionen">
      <div className="invoice-aktionen-reihe">
        {istBestaetigung && item.status === "gestellt" && (
          <button type="button" className="invoice-button primary" onClick={kundeHatBestaetigt} disabled={angebotZuordnen.isPending}
            title="Der Kunde hat unterschrieben oder per Mail zugestimmt — Angebot wird angenommen, Projekt aktiv">
            {angebotZuordnen.isPending ? "Wird vermerkt …" : "Vom Kunden bestätigt"}
          </button>
        )}
        {istAngebot && !telefon && (
          <button type="button" className={`invoice-button ${istBestaetigung && item.status === "gestellt" ? "quiet" : "primary"}`} onClick={() => starten("rechnung")} disabled={laeuft}>
            Als Rechnung übernehmen
          </button>
        )}
        {bestaetigbar && !telefon && (
          <button type="button" className="invoice-button quiet" onClick={() => starten("bestaetigung")} disabled={laeuft}
            title="Neuer Entwurf „Angebotsbestätigung“ mit denselben Positionen — der Kunde bestätigt ihn mit Unterschrift oder per Mail">
            Angebotsbestätigung
          </button>
        )}
        {/* Nur was Kontor selbst geschrieben hat, lässt sich neu erzeugen —
            Desktop- und Fremdbelege haben ihre Vorlage woanders. */}
        {item.web_festgeschrieben_am && !telefon && (
          <button type="button" className="invoice-button quiet" onClick={() => navigate(`/rechnungen/beleg/${item.id}/bearbeiten`)}>
            {belegArtLabel(item.kind, item.source_quote_nummer)} ändern
          </button>
        )}
        {!telefon && (
          <button type="button" className="invoice-button quiet" onClick={() => starten("kopie")} disabled={laeuft} title="Neuer Entwurf mit denselben Positionen — Nummer wird beim Festschreiben vergeben">
            Duplizieren
          </button>
        )}
        {istAngebot && item.projekt_id && !telefon && (
          <>
            <button type="button" className="invoice-button quiet" onClick={() => setAbschlagOffen(!abschlagOffen)} disabled={laeuft} aria-expanded={abschlagOffen}>
              Abschlagsrechnung
            </button>
            <button type="button" className="invoice-button quiet" onClick={() => starten("schluss")} disabled={laeuft}>
              Schlussrechnung
            </button>
          </>
        )}
        {stornierbar && !telefon && (
          <button type="button" className="invoice-button quiet" onClick={() => starten("storno")} disabled={laeuft}>
            Stornorechnung
          </button>
        )}
      </div>

      {istAngebot && !item.projekt_id && (
        <div className="invoice-followup-form invoice-projekt-form">
          <label className="invoice-field">
            <span>Projekt aus diesem Angebot</span>
            <input value={projektTitel} onChange={(event) => setProjektTitel(event.target.value)} />
          </label>
          <button type="button" className="invoice-button" onClick={projektAusAngebot} disabled={projektAnlegen.isPending || angebotZuordnen.isPending}>
            {projektAnlegen.isPending || angebotZuordnen.isPending ? "Projekt wird angelegt …" : "Projekt anlegen und Angebot zuordnen"}
          </button>
        </div>
      )}
      {istAngebot && item.projekt_id && abschlagOffen && !telefon && (
        <div className="invoice-followup-form">
          <p className="invoice-hint">Abschlag auf die Gesamtsumme des Angebots ({euro(item.netto)} netto).</p>
          <div className="invoice-abschlag-art" role="group" aria-label="Abschlag angeben als">
            <button type="button" className={`invoice-button ${abschlagArt === "prozent" ? "primary" : "quiet"}`} aria-pressed={abschlagArt === "prozent"}
              onClick={() => { setAbschlagArt("prozent"); setAbschlagWert("30"); }}>Prozent</button>
            <button type="button" className={`invoice-button ${abschlagArt === "betrag" ? "primary" : "quiet"}`} aria-pressed={abschlagArt === "betrag"}
              onClick={() => { setAbschlagArt("betrag"); setAbschlagWert(""); }}>Betrag (netto)</button>
          </div>
          <label className="invoice-field">
            <span>{abschlagArt === "prozent" ? "Anteil an der Gesamtsumme" : "Nettobetrag des Abschlags"}</span>
            <div className="invoice-money-input">
              <input
                type="number" inputMode="decimal" min="0" step={abschlagArt === "prozent" ? "1" : "0.01"}
                max={abschlagArt === "prozent" ? 100 : undefined}
                value={abschlagWert} onChange={(event) => setAbschlagWert(event.target.value)}
              />
              <b>{abschlagArt === "prozent" ? "%" : "€"}</b>
            </div>
          </label>
          {wert > 0 && (
            <p className="invoice-hint">
              {abschlagGueltig
                ? `Abschlag: ${euro(abschlagNetto)} netto${abschlagArt === "betrag" ? ` (${(abschlagNetto / item.netto * 100).toLocaleString("de-DE", { maximumFractionDigits: 2 })} %)` : ""}`
                : "Der Abschlag darf die Angebotssumme nicht übersteigen."}
            </p>
          )}
          <button type="button" className="invoice-button primary"
            onClick={() => starten("abschlag", abschlagArt === "prozent" ? { prozent: wert } : { netto: wert })}
            disabled={laeuft || !abschlagGueltig}>
            Abschlagsrechnung erstellen
          </button>
        </div>
      )}
      {laeuft && <p className="invoice-hint">Entwurf wird angelegt …</p>}
      {hinweis && <p className="invoice-hint">{hinweis}</p>}
    </section>
  );
}
